import { Injectable } from "@angular/core";
import { registerPlugin } from "@capacitor/core";

export interface CpuThermalZone {
	type: string;
	tempC: number;
}

/**
 * Worst values seen since tracking began, persisted on the device. Captures the
 * midday peak even though nobody is watching the kiosk screen at that time.
 */
export interface ThermalPeaks {
	/** Epoch millis when tracking started (or was last reset). */
	trackingSince: number;
	/** Highest CPU temp ever recorded, °C. Absent if never read. */
	peakCpuTemp?: number;
	/** Epoch millis when the peak CPU temp occurred. */
	peakCpuTempAt?: number;
	/** Lowest CPU temp ever recorded, °C. Absent if never read. */
	minCpuTemp?: number;
	/** Epoch millis when the lowest CPU temp occurred. */
	minCpuTempAt?: number;
	/** Worst OS throttling level ever recorded (0 = NONE … 6 = SHUTDOWN). */
	worstThermalStatus?: number;
	/** Label for worstThermalStatus (e.g. SEVERE). */
	worstThermalStatusLabel?: string;
	/** Epoch millis when the worst thermal status occurred. */
	worstThermalStatusAt?: number;
}

export interface ThermalInfo {
	/** OS thermal throttling level (0 = NONE … 6 = SHUTDOWN, -1 = unsupported). */
	thermalStatus: number;
	/** Human-readable label for thermalStatus (e.g. NONE, MODERATE, SEVERE). */
	thermalStatusLabel: string;
	/** Raw /sys/class/thermal zone readings. May be empty on locked-down devices. */
	cpuZones: CpuThermalZone[];
	/** Persisted worst-since-reset values for proving over-the-day overheating. */
	peaks: ThermalPeaks;
}

interface ThermalPlugin {
	getThermalInfo(): Promise<ThermalInfo>;
	resetPeaks(): Promise<void>;
}

const Thermal = registerPlugin<ThermalPlugin>("Thermal");

@Injectable({ providedIn: "root" })
export class ThermalService {
	async getThermalInfo(): Promise<ThermalInfo | null> {
		try {
			return await Thermal.getThermalInfo();
		} catch {
			// Plugin unavailable (e.g. running in browser/PWA) — caller shows N/A.
			return null;
		}
	}

	/** Highest CPU zone temperature, or null if no zones are readable. */
	maxCpuTemp(info: ThermalInfo | null): number | null {
		if (!info?.cpuZones?.length) {
			return null;
		}
		return info.cpuZones.reduce((max, z) => (z.tempC > max ? z.tempC : max), -Infinity);
	}

	/** Clears the recorded peaks to start a fresh measurement period. */
	async resetPeaks(): Promise<void> {
		try {
			await Thermal.resetPeaks();
		} catch {
		}
	}
}
